<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type {
  FieldPart,
  PartsTree,
  PartsTreeNode,
} from "@/types/field-observations.types";
import { useCreateFieldPartTemplate } from "@/stores/queries/field-observations.query";
import FieldPartTemplateTreePreview from "@/components/field-part-templates/FieldPartTemplateTreePreview.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  modelValue: boolean;
  part: FieldPart | null;
  allParts: FieldPart[];
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { t } = useTranslation();

const name = ref("");
const description = ref("");

const { mutate: createTemplate, isPending } = useCreateFieldPartTemplate();

// Build tree:
// - If a `part` is provided: use its direct children as top-level nodes
//   (the part's name is used only as the template title).
// - If no `part` is provided: save the whole tree (all root parts).
const buildTree = computed((): PartsTree => {
  const buildNode = (part: FieldPart): PartsTreeNode => {
    const children = props.allParts.filter((p) => p.parentId === part.id);
    return {
      name: part.name,
      children: children.map(buildNode),
    };
  };

  const topLevel = props.part
    ? props.allParts.filter((p) => p.parentId === props.part?.id)
    : props.allParts.filter((p) => !p.parentId);

  return {
    parts: topLevel.map(buildNode),
  };
});

// Reset form when modal opens
watch(
  () => props.modelValue,
  (isOpen) => {
    if (isOpen) {
      name.value = props.part?.name ?? "";
      description.value = "";
    }
  },
);

const close = () => {
  emit("update:modelValue", false);
};

const handleSave = () => {
  if (!name.value.trim() || buildTree.value.parts.length === 0) return;

  createTemplate(
    {
      name: name.value.trim(),
      description: description.value.trim() || undefined,
      tree: buildTree.value,
    },
    {
      onSuccess: () => {
        close();
      },
    },
  );
};
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="popup-overlay" @click.self="close">
      <div class="popup-content">
        <h4>
          {{ t("dashboard:fieldObservations.partTemplates.saveModal.title") }}
        </h4>

        <input
          v-model="name"
          type="text"
          class="popup-input"
          :placeholder="
            t(
              'dashboard:fieldObservations.partTemplates.saveModal.namePlaceholder',
            )
          "
          @keyup.enter="handleSave"
        />

        <textarea
          v-model="description"
          class="popup-input mt-3"
          :placeholder="
            t(
              'dashboard:fieldObservations.partTemplates.saveModal.descriptionPlaceholder',
            )
          "
          rows="2"
        />

        <!-- Preview -->
        <div v-if="buildTree.parts.length > 0" class="preview-section mt-3">
          <div class="preview-label">
            <PhosphorIcon name="eye" :size="16" class="me-1" />
            {{
              t("dashboard:fieldObservations.partTemplates.saveModal.preview")
            }}
          </div>
          <div class="preview-tree">
            <FieldPartTemplateTreePreview :nodes="buildTree.parts" />
          </div>
        </div>

        <div class="popup-actions">
          <button class="popup-btn cancel" @click="close">
            {{ t("cancel") }}
          </button>
          <button
            class="popup-btn confirm"
            :disabled="
              !name.trim() || buildTree.parts.length === 0 || isPending
            "
            @click="handleSave"
          >
            <span
              v-if="isPending"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ t("dashboard:fieldObservations.partTemplates.saveModal.save") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}

.popup-content {
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  background-color: var(--bs-white);
  border-radius: 20px 20px 0 0;
  padding: 24px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));

  h4 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
  }
}

.popup-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background-color: var(--bs-tertiary-bg);
  font-size: 16px;
  color: var(--bs-body-color);
  resize: none;

  &:focus {
    outline: none;
    border-color: var(--bs-primary);
  }
}

.preview-section {
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  overflow: hidden;
}

.preview-label {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: var(--bs-tertiary-bg);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color);
}

.preview-tree {
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
}

.popup-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.popup-btn {
  flex: 1;
  padding: 14px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);
  }

  &.confirm {
    background-color: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
